import ElementController from "../base-controllers/ElementController.js";
import { blobToDataURL } from "../../utils/data_url.js";
import { dataURLtoFile } from "../../utils/file.js";
import { del, get, set } from "../../utils/idb.js";
import { FormEvent } from "../components/form/constants.js";
import { MAX_IMAGE_DIM } from "../../constants.js";
import { setFile } from "../utils/dom.js";
import { resizeImageFileToFile } from "../utils/image.js";
import { inventoryDraft } from "./InventoryPhotoHandoff.js";
import { dismissOverlay } from "../utils/overlay.js";
import { isPreview, replaceHistory, visit } from "../utils/turbo.js";
import { gettext } from "../utils/i18n.js";
import { nextRepaint } from "../utils/js.js";
import { setStaticAttr } from "../utils/morph/helpers.js";
import { SpinnerSVG } from "./Spinner.js";
import { Camera } from "../components/furniture-item/camera.js";
import "../components/furniture-item/camera-modal.js";
export class LibraryPhotoFilter extends ElementController {
    init() {
        const input = document.querySelector("#furniture-items input[name=image_query]");
        if (isPreview() || !this.root.dataset.photoFilter)
            return;
        const createUrl = this.root.dataset.photoCreateUrl;
        if (createUrl) {
            const target = new URL(createUrl, location.href);
            if (target.origin === location.origin) {
                this.root.removeAttribute("data-photo-filter");
                this.root.dispatchEvent(new CustomEvent("library-photo:persisted", { bubbles: true }));
                visit(target.href);
                return;
            }
        }
        if (!input?.form)
            return;
        input.value = this.root.dataset.photoFilter;
        // History snapshots must not replay a completed handoff.
        this.root.removeAttribute("data-photo-filter");
        input.setAttribute("value", input.value);
        this.root.dispatchEvent(new CustomEvent("library-photo:persisted", { bubbles: true }));
        for (const name of ["similar_to", "appearance", "page"]) {
            const old = input.form.querySelector(`input[name="${name}"]`);
            if (old) {
                old.value = "";
                old.setAttribute("value", "");
            }
        }
        if (this.root.hasAttribute("data-photo-preserve")) {
            // Keep the saved image's URL, then refresh its scoped results as well.
            // The ordinary filter GET reads the stored failure; it does not retry AI.
            const url = new URL(location.href);
            url.searchParams.set("image_query", input.value);
            for (const name of ["similar_to", "appearance", "page"])
                url.searchParams.delete(name);
            replaceHistory(url.href);
        }
        const dialog = this.root.closest("dialog");
        if (dialog)
            dismissOverlay(dialog);
        const photoId = input.value;
        // The result morph removes this carrier. Its sending form owns the
        // one-shot completion listener, so destroying the carrier cannot cancel it.
        input.form.addEventListener(FormEvent.sendEnd, async (event) => {
            if (event.detail?.success === false)
                return;
            await window.hasPageUpdatesPromise;
            await nextRepaint();
            const current = document.querySelector('#furniture-items input[name="image_query"]');
            if (current?.value !== photoId || !matchMedia("(max-width: 768px)").matches
                || document.querySelector('[data-existing-visual][data-visual-error], dialog[open]'))
                return;
            const target = document.querySelector('[data-inventory-view]')
                ?? document.querySelector('#furniture-items [data-library-result-count]');
            target?.scrollIntoView({ block: "start", behavior: "smooth" });
        }, { once: true });
        input.form.requestSubmit();
    }
}
LibraryPhotoFilter.id = "LibraryPhotoFilter";
/** Uses the ordinary filter request; never sends an additional AI request. */
export class LibrarySearchStatus extends ElementController {
    constructor() {
        super(...arguments);
        this.previousMinHeight = "";
        this.previousDescriptionMinHeight = "";
        this.retryingImage = (event) => {
            if (!(event.target instanceof Element) || !event.target.hasAttribute("data-photo-search-retry"))
                return;
            const panel = this.visual;
            if (!panel)
                return;
            this.retryPanel = panel;
            this.previousMinHeight = panel.style.minHeight;
            panel.style.minHeight = `${panel.getBoundingClientRect().height}px`;
            // These are temporary server-owned attributes, not morph-preserved client
            // state. A successful response can remove the retry button before its end
            // event bubbles, so the returned fragment must clear them on its own.
            setStaticAttr(panel, "style", panel.getAttribute("style"));
            setStaticAttr(panel, "aria-busy", "true");
            const description = panel.querySelector("[data-visual-description]");
            if (description) {
                this.retryDescription = description;
                this.previousDescriptionMinHeight = description.style.minHeight;
                description.style.minHeight = `${description.getBoundingClientRect().height}px`;
                setStaticAttr(description, "style", description.getAttribute("style"));
            }
            const title = panel.querySelector("[data-visual-title]");
            if (title)
                title.textContent = gettext("AI is searching…");
            const message = panel.querySelector("[data-visual-message]");
            if (message) {
                message.textContent = gettext("Retrieving results…");
                message.setAttribute("role", "status");
            }
            for (const button of panel.querySelectorAll("button"))
                setStaticAttr(button, "disabled", "");
            const form = this.form;
            if (form && !form.inert) {
                setStaticAttr(form, "inert", "");
                this.retryFilter = form;
            }
        };
        this.retriedImage = (event) => {
            if (!(event.target instanceof Element) || !event.target.hasAttribute("data-photo-search-retry"))
                return;
            const panel = this.visual;
            if (panel) {
                if (panel === this.retryPanel) {
                    panel.style.minHeight = this.previousMinHeight;
                    if (this.retryDescription?.isConnected)
                        this.retryDescription.style.minHeight = this.previousDescriptionMinHeight;
                }
                panel.removeAttribute("aria-busy");
                const title = panel.querySelector("[data-visual-title]");
                if (title?.textContent === gettext("AI is searching…")) {
                    title.textContent = gettext("Image search failed");
                    const message = panel.querySelector("[data-visual-message]");
                    if (message) {
                        message.textContent = gettext("The search could not be completed. Try again.");
                        message.setAttribute("role", "alert");
                    }
                }
                for (const button of panel.querySelectorAll("button"))
                    button.disabled = false;
            }
            this.retryPanel = undefined;
            this.retryDescription = undefined;
            if (this.retryFilter)
                this.retryFilter.inert = false;
            this.retryFilter = undefined;
        };
        this.sending = (event) => {
            if (event.target !== this.form)
                return;
            for (const button of this.root.querySelectorAll("[data-photo-camera], [data-photo-gallery]"))
                button.disabled = true;
            // Form controls are temporarily disabled while sending; their live values
            // still describe this request, whereas native FormData would omit them.
            const value = (name) => this.form?.querySelector(`[name="${name}"]`)?.value;
            if (!value("appearance") && !value("image_query") && !value("similar_to"))
                return;
            const photo = this.root.querySelector("[data-photo-work]");
            if (photo && !photo.hidden)
                return;
            const work = this.work;
            if (!work)
                return;
            work.hidden = false;
            const old = this.root.querySelector("[data-existing-visual]");
            if (old)
                setStaticAttr(old, "hidden", "");
            const status = work.querySelector("[data-visual-status]");
            status.textContent = value("appearance")
                ? gettext("AI is searching…")
                : gettext("Retrieving results…");
            status.setAttribute("role", "status");
            work.querySelector("[data-visual-retry]").hidden = true;
        };
        this.sent = (event, result) => {
            if (event.target !== this.form)
                return;
            for (const button of this.root.querySelectorAll("[data-photo-camera], [data-photo-gallery]"))
                button.disabled = false;
            if (result?.success !== false)
                return;
            const work = this.work;
            if (!work)
                return;
            work.hidden = false;
            const status = work.querySelector("[data-visual-status]");
            status.textContent = gettext("The search could not be completed. Try again.");
            status.setAttribute("role", "alert");
            work.querySelector("[data-visual-retry]").hidden = false;
        };
        this.retry = () => this.form?.requestSubmit();
    }
    get visual() { return this.root.querySelector("[data-existing-visual]"); }
    get form() { return this.root.querySelector("input[name=appearance]")?.form; }
    destroy() { if (this.retryFilter)
        this.retryFilter.inert = false; }
    get work() { return this.root.querySelector("[data-visual-busy]"); }
}
LibrarySearchStatus.id = "LibrarySearchStatus";
LibrarySearchStatus.binds = {
    root: [["form:sendStart", "sending"], ["form:sendEnd", "sent"], ["action:beforeSubmission", "retryingImage"], ["action:afterSubmission", "retriedImage"]],
    "[data-visual-retry]": [["click", "retry"]],
};
class LibraryPhoto extends ElementController {
    constructor() {
        super(...arguments);
        this.preparing = false;
        this.ready = false;
        this.isSending = false;
        this.intent = "";
        this.draftWrite = Promise.resolve();
        this.finishRequested = (event) => {
            if (!this.input.files?.length)
                return;
            if (event.target.hasAttribute("data-inventory-finish-confirm")) {
                void this.cancel();
                return;
            }
            event.preventDefault();
            const dialog = document.querySelector("#finish-inventory-session-dialog");
            const warning = dialog?.querySelector("[data-inventory-photo-close-warning]");
            if (warning)
                warning.hidden = false;
            if (dialog && !dialog.open)
                dialog.showModal();
        };
        this.clearDraft = async () => {
            const key = this.draftKey;
            if (!key)
                return;
            this.draftWrite = this.draftWrite.catch(() => { }).then(() => del("form", key));
            await this.draftWrite;
        };
        this.startRequested = (event) => {
            const picker = this.root.querySelector("[popover][id]");
            if (!picker?.id || event.currentTarget.getAttribute("data-photo-start") !== picker.id)
                return;
            if (event.currentTarget instanceof HTMLAnchorElement) {
                if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
                    return;
                if (new URL(event.currentTarget.href).origin !== location.origin)
                    return;
                if (this.startKey) {
                    try {
                        sessionStorage.setItem(this.startKey, String(Date.now()));
                    }
                    catch { /* Optional. */ }
                }
                return;
            }
            event.preventDefault();
            this.startPicker();
        };
        this.beforeSend = (event) => {
            if (this.inline && !this.intent) {
                event.preventDefault();
                this.restoreForm();
            }
        };
        this.sending = (_event, detail) => {
            this.activeRequest = detail?.formRequest;
            this.isSending = true;
            this.toggleChoices(true);
            this.toggleSubmit(true);
            this.photoBusy(true);
            this.setStatus(this.root.dataset.photoSendingStatus ?? gettext("AI is searching…"));
        };
        this.sent = (_event, result) => {
            this.activeRequest = undefined;
            this.unlockFilter();
            this.isSending = false;
            this.toggleChoices(false);
            this.toggleSubmit(!this.ready);
            if (result?.success === false || this.work?.hasAttribute("data-photo-error"))
                this.photoBusy(false);
            if (this.inline && this.work?.hidden)
                return;
            if (result?.success === false && !this.work?.hasAttribute("data-photo-error")) {
                this.setStatus(gettext("The search could not be completed. Try again."), true);
                const retry = this.root.querySelector("[data-photo-retry]");
                if (retry)
                    retry.hidden = false;
            }
            const preview = this.root.querySelector("[data-photo-preview]");
            if (preview && this.previewUrl && !preview.getAttribute("src")) {
                preview.src = this.previewUrl;
                preview.hidden = false;
            }
        };
        this.retrying = (event) => {
            if (!event.target.hasAttribute("data-photo-server-retry"))
                return;
            this.sending();
        };
        this.retried = (event) => {
            if (!this.isSending || event.target.dataset.photoRequest !== this.intent)
                return;
            const failed = this.status?.textContent === gettext("AI is searching…");
            this.sent(undefined, { success: !failed });
            if (failed) {
                this.setStatus(gettext("The search could not be completed. Try again."), true);
            }
        };
        this.retry = () => { if (this.ready && !this.isSending)
            this.input.form?.requestSubmit(); };
        this.cancel = async () => {
            this.intent = "";
            this.activeRequest?.stopFetchRequest();
            this.restoreForm();
            this.unlockFilter();
            this.ready = false;
            this.preparing = false;
            this.isSending = false;
            if (this.work) {
                this.work.hidden = true;
                this.work.dataset.photoRequest = "";
            }
            const previous = this.existingVisual;
            if (previous)
                previous.hidden = false;
            this.toggleChoices(false);
            this.toggleSubmit(true);
            this.photoBusy(false);
            this.confirmUnsavedPhoto(false);
            setFile(this.input, null);
            await this.clearDraft();
        };
        this.camera = async () => {
            if (this.cameraDialog || this.isSending || this.preparing)
                return;
            if (!await new Camera({ useCamera: true }).canUseCamera()) {
                this.input.setAttribute("capture", "environment");
                this.input.click();
                return;
            }
            if (!this.root.isConnected)
                return;
            const dialog = document.createElement("dialog");
            dialog.setAttribute("data-slot", "dialog");
            dialog.setAttribute("data-camera-dialog", "");
            dialog.setAttribute("aria-label", gettext("Search with photo"));
            const content = document.createElement("furniture-item-camera-modal");
            content.onCapture = async (image) => {
                setFile(this.input, new File([image], "search.jpg", { type: image.type || "image/jpeg" }));
                dialog.close();
                await this.selected();
            };
            content.onChooseFile = this.gallery;
            dialog.append(content);
            dialog.addEventListener("click", event => {
                if (event.target.closest('[data-action="close"]'))
                    dialog.close();
            });
            dialog.addEventListener("close", () => { dialog.remove(); this.cameraDialog = undefined; }, { once: true });
            this.cameraDialog = dialog;
            document.body.append(dialog);
            dialog.showModal();
        };
        this.gallery = () => { if (!this.isSending && !this.preparing) {
            this.input.removeAttribute("capture");
            this.input.click();
        } };
        this.selected = async () => {
            const file = this.input.files?.[0];
            if (!file || this.preparing || this.isSending)
                return;
            this.cameraDialog?.close();
            const picker = this.root.querySelector("[popover]");
            if (picker?.matches(":popover-open"))
                picker.hidePopover();
            this.confirmUnsavedPhoto(true);
            const intent = this.intent = crypto.randomUUID();
            const nonce = this.root.querySelector("[name=photo-request_id]");
            if (nonce)
                nonce.value = intent;
            if (this.work) {
                this.work.hidden = false;
                this.work.dataset.photoRequest = intent;
                this.work.removeAttribute("data-photo-error");
            }
            if (this.inline) {
                const filter = document.querySelector("#furniture-items input[name=image_query]")?.form;
                if (filter && !filter.inert) {
                    this.lockedFilter = filter;
                    filter.inert = true;
                }
                const old = this.existingVisual;
                if (old)
                    setStaticAttr(old, "hidden", "");
            }
            this.preparing = true;
            this.ready = false;
            this.toggleSubmit(true);
            this.toggleChoices(true);
            this.photoBusy(true);
            this.setStatus(gettext("Preparing the image…"));
            try {
                if (file.size > 20 * 1024 * 1024) {
                    throw new Error(gettext("The image can be at most 20 MB."));
                }
                // Show the captured image before resizing/storage awaits; only its bytes
                // change later, never the card's preview dimensions.
                this.showPreview(file);
                const prepared = await resizeImageFileToFile(file, MAX_IMAGE_DIM, MAX_IMAGE_DIM, "max", false);
                if (!this.root.isConnected || this.intent !== intent)
                    return;
                setFile(this.input, prepared);
                try {
                    await this.saveDraft(prepared, intent);
                }
                catch { /* A valid photo can still be persisted server-side without local storage. */ }
                if (!this.root.isConnected || this.intent !== intent)
                    return;
                this.showPreview(prepared);
                if (!this.root.hasAttribute("data-auto-search")) {
                    this.setStatus(gettext("The image is ready."));
                }
                this.ready = true;
                this.toggleSubmit(false);
                const original = document.querySelector("form[data-inventory-draft]");
                const draftInput = this.root.querySelector("[name=photo-draft]");
                if (original && draftInput)
                    draftInput.value = JSON.stringify(inventoryDraft(original));
                if (this.root.hasAttribute("data-auto-search"))
                    this.input.form?.requestSubmit();
            }
            catch {
                if (this.intent === intent) {
                    this.unlockFilter();
                    this.setStatus(gettext("The image could not be read. Choose a JPEG or PNG image, at most 20 MB."), true);
                    this.photoBusy(false);
                }
            }
            finally {
                if (this.intent === intent) {
                    this.preparing = false;
                    this.toggleChoices(this.isSending);
                    this.toggleSubmit(!this.ready || this.isSending);
                }
            }
        };
    }
    unlockFilter() { if (this.lockedFilter)
        this.lockedFilter.inert = false; this.lockedFilter = undefined; }
    get inline() { return this.root.hasAttribute("data-library-inline"); }
    get input() { return this.root.querySelector("input[type=file]"); }
    get work() { return this.root.querySelector("[data-photo-work]"); }
    get status() { return this.root.querySelector("[data-photo-status]"); }
    init() {
        this.toggleSubmit(true);
        this.intent = this.work?.dataset.photoRequest || "";
        if (this.intent && !this.root.querySelector("[data-photo-server-retry]"))
            this.cancel();
        // Morphing library → inventory keeps this controller but changes the
        // picker's id. Resolve the current target on click, not only at init.
        this.onGlobal("click", this.startRequested, { delegate: "[data-photo-start]" });
        this.onGlobal("action:beforeSubmission", this.finishRequested, { delegate: "[data-empty-inventory-finish], [data-inventory-finish-confirm]" });
        // Consume only a recent, deliberate start; reload/back must not reopen it.
        if (!isPreview() && this.startKey) {
            try {
                const started = sessionStorage.getItem(this.startKey);
                sessionStorage.removeItem(this.startKey);
                if (started && Date.now() - Number(started) < 10000)
                    this.startPicker();
            }
            catch { /* The manual header control remains available. */ }
        }
        if (!isPreview())
            void this.restoreDraft();
    }
    get draftKey() {
        const scope = this.root.dataset.photoStartKey;
        return scope ? `library-photo-draft:${scope}` : "";
    }
    async saveDraft(file, intent) {
        const key = this.draftKey;
        if (!key)
            return;
        const value = await blobToDataURL(file);
        if (!this.root.isConnected || this.intent !== intent)
            return;
        const data = [
            { name: "photo-image", value, fileName: file.name },
            { name: "image_query", value: new URL(location.href).searchParams.get("image_query") || "" },
        ];
        this.draftWrite = this.draftWrite.catch(() => { }).then(() => set("form", data, key));
        await this.draftWrite;
    }
    async restoreDraft() {
        if (!this.draftKey)
            return;
        try {
            const data = await get("form", this.draftKey);
            if (!Array.isArray(data) || !this.root.isConnected || this.intent || this.input.files?.length)
                return;
            const photo = data.find(row => row?.name === "photo-image");
            const source = data.find(row => row?.name === "image_query");
            if (source?.value !== (new URL(location.href).searchParams.get("image_query") || "")) {
                await this.clearDraft();
                return;
            }
            if (typeof photo?.fileName !== "string" || typeof photo.value !== "string" || !photo.value.startsWith("data:image/"))
                return;
            const file = dataURLtoFile(photo.value, photo.fileName);
            this.preparing = true;
            try {
                setFile(this.input, file);
            }
            finally {
                this.preparing = false;
            }
            this.intent = crypto.randomUUID();
            const nonce = this.root.querySelector("[name=photo-request_id]");
            if (nonce)
                nonce.value = this.intent;
            if (this.work) {
                this.work.hidden = false;
                this.work.dataset.photoRequest = this.intent;
            }
            this.previewUrl = URL.createObjectURL(file);
            const preview = this.root.querySelector("[data-photo-preview]");
            if (preview) {
                preview.src = this.previewUrl;
                preview.hidden = false;
            }
            const previous = this.existingVisual;
            if (previous)
                setStaticAttr(previous, "hidden", "");
            this.ready = true;
            this.confirmUnsavedPhoto(true);
            this.setStatus(gettext("The image is ready."));
            const retry = this.root.querySelector("[data-photo-retry]");
            if (retry)
                retry.hidden = false;
            this.toggleSubmit(false);
            this.photoBusy(false);
            // Restoration is preview-only: no camera permission or automatic AI retry.
        }
        catch { /* Storage is optional; a server-owned image remains in the URL. */ }
    }
    get existingVisual() {
        const scope = this.root.closest("#inventory-session") ?? document.querySelector("#furniture-items");
        return scope?.querySelector("[data-existing-visual]");
    }
    get startKey() {
        const scope = this.root.dataset.photoStartKey;
        return scope ? `library-photo-start:${scope}` : "";
    }
    startPicker() {
        if (this.root.dataset.photoMobile === "true")
            void this.camera();
        else
            this.gallery();
    }
    setStatus(text, error = false) {
        const status = this.status;
        if (!status)
            return;
        status.textContent = text;
        status.setAttribute("role", error ? "alert" : "status");
        if (this.work) {
            if (error)
                setStaticAttr(this.work, "data-visual-error", "");
            else
                this.work.removeAttribute("data-visual-error");
            const title = this.work.querySelector("[data-photo-title]");
            if (title)
                title.textContent = error ? gettext("Image search failed") : gettext("Image search");
        }
    }
    confirmUnsavedPhoto(active) {
        const finish = this.root.closest("#inventory-session")?.querySelector("[data-empty-inventory-finish]");
        if (!finish)
            return;
        if (active) {
            finish.dataset.confirmTitle = gettext("Finish inventory?");
            finish.dataset.confirmMessage = gettext("The current image search will close.");
            finish.dataset.confirmText = gettext("Finish inventory");
        }
        else {
            delete finish.dataset.confirmTitle;
            delete finish.dataset.confirmMessage;
            delete finish.dataset.confirmText;
        }
    }
    photoBusy(active) {
        const button = this.work?.querySelector("[data-photo-retry]");
        if (!button)
            return;
        button.hidden = false;
        button.disabled = active || !this.ready;
        if (active) {
            button.setAttribute("aria-busy", "true");
            if (!button.querySelector('[data-slot="spinner"]'))
                button.insertAdjacentHTML("afterbegin", SpinnerSVG.sm);
        }
        else {
            button.removeAttribute("aria-busy");
            button.querySelector('[data-slot="spinner"]')?.remove();
        }
    }
    showPreview(file) {
        if (this.previewUrl)
            URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = URL.createObjectURL(file);
        const preview = this.root.querySelector("[data-photo-preview]");
        if (preview) {
            preview.src = this.previewUrl;
            preview.hidden = false;
        }
    }
    restoreForm() { this.input.form?.dispatchEvent(new CustomEvent(FormEvent.enableFields, { bubbles: true })); }
    toggleSubmit(disabled) {
        for (const element of Array.from(this.input.form?.elements || [])) {
            if (element instanceof HTMLButtonElement && element.type === "submit")
                element.disabled = disabled;
        }
    }
    toggleChoices(disabled) {
        for (const button of this.root.querySelectorAll("[data-photo-camera], [data-photo-gallery], [data-photo-retry], [data-photo-server-retry]"))
            button.disabled = disabled;
    }
    destroy() {
        this.unlockFilter();
        this.intent = "";
        this.activeRequest?.stopFetchRequest();
        this.cameraDialog?.close();
        if (this.previewUrl)
            URL.revokeObjectURL(this.previewUrl);
    }
}
LibraryPhoto.id = "LibraryPhoto";
LibraryPhoto.binds = {
    "[data-photo-camera]": [["click", "camera"]],
    "[data-photo-gallery]": [["click", "gallery"]],
    "[data-photo-cancel]": [["click", "cancel"]],
    "[data-photo-retry]": [["click", "retry"]],
    "input[type=file]": [["change", "selected"]],
    form: [["form:beforeSend", "beforeSend"], ["form:sendStart", "sending"], ["form:sendEnd", "sent"]],
    root: [["action:beforeSubmission", "retrying"], ["action:afterSubmission", "retried"], ["library-photo:persisted", "clearDraft"]],
};
export default LibraryPhoto;
//# sourceMappingURL=LibraryPhoto.js.map